import { Helmet } from "react-helmet-async";
import AuthLayout from "@/features/auth/auth-layout";
import SignUpStep1Form, {
  SignUpStep1FormFields,
} from "@/features/auth/sign-up/sign-up-step1-form";
import SignUpStep2Form, {
  SignUpStep2FormFields,
} from "@/features/auth/sign-up/sign-up-step2-form";
import SocialButtonGroup from "@/features/auth/social-buttons/social-button-group";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router";
import { Box, Text } from "@chakra-ui/react";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import SignUpStep3Form, {
  SignUpStep3FormFields,
} from "@/features/auth/sign-up/sign-up-step3-form";
import SignUpStep4Form, {
  SignUpStep4FormFields,
} from "@/features/auth/sign-up/sign-up-step4-form";
import { resolveReferral } from "@/features/auth/sign-up/sign-up-referral";
import { getCanonicalUrl } from "@/shared/utils/canonical-url";

export type SignUpFormValues = SignUpStep1FormFields &
  SignUpStep2FormFields &
  SignUpStep3FormFields &
  SignUpStep4FormFields;

function getStep(raw: string | null) {
  switch (raw) {
    case "4":
      return 4;
    case "3":
      return 3;
    case "2":
      return 2;
    default:
      return 1;
  }
}

function getIsValidStep(step: 1 | 2 | 3 | 4, formValues: SignUpFormValues) {
  if (step === 4) {
    return (
      formValues.name &&
      formValues.userInputedCompanyName &&
      formValues.businessRegistrationNumber
    );
  }
  if (step === 3) {
    return (
      formValues.email &&
      formValues.email !== "" &&
      formValues.password &&
      formValues.password !== "" &&
      formValues.passwordConfirmation &&
      formValues.passwordConfirmation !== ""
    );
  }
  if (step === 2) {
    return (
      formValues.mobileNumber &&
      formValues.mobileNumber !== "" &&
      formValues.tempToken &&
      formValues.tempToken !== ""
    );
  }
  // step 1
  return true;
}

export default function SignUpPage({ step }: { step: 1 | 2 | 3 | 4 }) {
  const navigate = useNavigate();
  // 초기값은 lazy 로 만든다 — 그냥 넘기면 resolveReferral() 이 렌더마다 다시 돌아
  // 매번 URL 을 읽고 sessionStorage 에 쓴다.
  const [formValues, setFormValues] = useState<SignUpFormValues>(() => ({
    email: "",
    password: "",
    passwordConfirmation: "",
    // 3단계에서 사업자등록증을 업로드해 파싱 결과로 채우는 값들.
    // step4의 ...restValues 스프레드에서는 제외해 회원가입 본문에 실리지 않게 한다.
    businessRegistrationNumber: "",
    businessRegistrationFile: null,
    businessRegistrationCertificateFileId: null,
    isBusinessRegistrationManualEntry: false,
    userInputedCompanyName: "",
    // 가입 URL 의 ?ref= 로 들어온 추천인을 3단계 폼의 초기값으로 채워준다.
    recommender: resolveReferral(),
    name: "",
    countryCode: "+82",
    mobileNumber: "",
    mobileVerificationCode: "",
    tempToken: "",
    agreeToAll: false,
    agreeToTermsOfUse: false,
    agreeToCollectPersonalInformation: false,
    agreeToProvidePersonalInformation: false,
    agreeToMarketing: false,
    over14: false,
  }));

  const isValidStep = getIsValidStep(step, formValues);
  const { tTitle, tSentence } = useI18nHelper();

  // useEffect(() => {
  //   if (step === 2) {
  //     const handleBeforeUnload = (event: BeforeUnloadEvent) => {
  //       event.preventDefault();
  //       event.returnValue = "";
  //     };
  //
  //     window.addEventListener("beforeunload", handleBeforeUnload);
  //     return () => {
  //       window.removeEventListener("beforeunload", handleBeforeUnload);
  //     };
  //   }
  // }, [step]);

  useEffect(() => {
    if (!isValidStep) {
      navigate("/auth/sign-up", { replace: true });
      //navigate(0);
    }
  }, [isValidStep, navigate]);

  if (!isValidStep) {
    return <>wrong.. redirect..</>;
  }

  const tail = (
    <Box fontSize={"12px"}>
      {tSentence("already_have_an_account", { ns: "auth" })} &nbsp;
      <Link to={"/auth/sign-in"}>
        <Text as="u" fontWeight={"700"}>
          {tSentence("sign_in.do", { ns: "auth" })}
        </Text>
      </Link>
    </Box>
  );

  // const handleSubmit = (step1FormValues: SignUpStep1FormFields) => {
  //   setFormValues({ ...formValues, ...step1FormValues });
  //   navigate("/auth/sign-up/2");
  // };

  const handleSubmitStep1 = (step1FormValues: SignUpStep1FormFields) => {
    setFormValues({ ...formValues, ...step1FormValues });
    navigate("/auth/sign-up/2");
  };

  const handleSubmitStep2 = (step2FormValues: SignUpStep2FormFields) => {
    setFormValues({ ...formValues, ...step2FormValues });
    navigate("/auth/sign-up/3");
  };

  const handleSubmitStep3 = (step3FormValues: SignUpStep3FormFields) => {
    setFormValues({ ...formValues, ...step3FormValues });
    navigate("/auth/sign-up/4");
  };

  const form = (() => {
    switch (step) {
      case 4:
        return <SignUpStep4Form initialValue={formValues} />;
      case 3:
        return (
          <SignUpStep3Form
            initialValue={formValues}
            onSubmit={handleSubmitStep3}
          />
        );
      case 2:
        return (
          <SignUpStep2Form
            initialValue={formValues}
            onSubmit={handleSubmitStep2}
          />
        );
      case 1:
      default:
        return (
          <SignUpStep1Form
            initialValue={formValues}
            onSubmit={handleSubmitStep1}
          />
        );
    }
    // if (step === 1) {
    //   return (
    //     <SignUpStep1Form initialValue={formValues} onSubmit={handleSubmit} />
    //   );
    // } else {
    //   return <SignUpStep2Form initialValue={formValues} bySocial={false} />;
    // }
  })();

  return (
    <AuthLayout
      title={tTitle("sign_up", { ns: "auth" })}
      tail={tail}
      indicator={`Step ${step} of 4`}
      socialButtons={step === 1 && <SocialButtonGroup name={"sign up"} />}
    >
      <Helmet>
        <title>Pathrade | Sign Up</title>
        <meta
          name="description"
          content="Sign up for the Pathrade to use a B2B Fashion Platform tailored for the global luxury fashion landscape with a seamless ordering experience and insightful analytics."
        />
        <link
          rel="canonical"
          href={getCanonicalUrl()}
        />
        <meta
          property="og:title"
          content="Pathrade | Sign up to the Luxury Fashion B2B Platform & Saas."
        />
        <meta
          property="og:description"
          content="Sign up for the Pathrade to use a B2B Fashion Platform tailored for the global luxury fashion landscape with a seamless ordering experience and insightful analytics."
        />
        <meta
          property="og:url"
          content={getCanonicalUrl()}
        />
      </Helmet>
      {form}
    </AuthLayout>
  );
}
